import Markdown from 'markdown-to-jsx';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';

interface OscarMarkdownProps {
	children: string;
	className?: string;
}

/**
 * Renders an agent answer as markdown.
 *
 * Models format by default — `**bold**`, `- lists`, and pipe tables — and the
 * harness sends that through as a plain string, so without this the raw syntax
 * shows on screen (a schedule table arrives as a wall of pipes).
 *
 * Every tag is mapped to the project's typography and colour tokens so answers
 * match the rest of the panel rather than browser defaults. `markdown-to-jsx`
 * builds React elements and drops raw HTML, so nothing here can inject markup
 * from model output.
 *
 * Headings render as spans carrying the project's heading classes, never `h1`–`h6`.
 * `typography.less` sets `font-size` on every heading element — and on `.h1`–`.h6` —
 * with `!important`, which no class can beat: an `# h1` came out at display size
 * inside a 24px line box and overlapped the paragraph above it. The `.hNmedium`
 * classes are a different selector, so they apply cleanly.
 *
 * The scale tops out at `h4medium` (20px), one step **below** the empty-state
 * greeting at 24px. An answer's heading should not be as loud as the panel's own
 * welcome, and it previously tied it exactly — 24px inside a 14px conversation,
 * with nothing in between, which is what made the type read as two sizes rather
 * than a hierarchy. `h1medium` at 38px is page-chrome sizing and never applies.
 *
 * The ramp is 20 / 16 / 14-medium against 14-regular body. Below `h3` there is
 * nowhere left to go — body is 14 and `**bold**` is already 14/500 — so the
 * deeper levels separate on colour (`grey-9`) rather than size. Models rarely
 * emit them.
 *
 * Headings also carry top margin — every level, not just the first three. The
 * container spaces everything with a uniform `gap`, which gives a heading as much
 * room below as above and leaves it floating between two blocks instead of
 * introducing the one beneath it.
 */
const heading = (className: string) => ({
	component: 'span' as const,
	props: { className },
});

/**
 * A table in its own scroll box.
 *
 * `markdown-to-jsx` renders the `<table>` as a direct child of the flex column,
 * so a wide one — the schedule tables this renderer exists for — stretched the
 * column and pushed the whole panel sideways. Overflow has to live on a wrapper;
 * putting it on the table itself does nothing.
 *
 * Declared out here rather than inline in `overrides` so it is one component
 * identity across renders, instead of a new one on every streamed token.
 */
const ScrollableTable = ({ children, ...props }: React.PropsWithChildren<{}>) => (
	// The wrapper owns the frame as well as the overflow: a rounded, bordered box
	// reads as one object, and `overflow-x-auto` already clips, so the corners cut
	// cleanly without a separate `overflow-hidden`.
	<div className="w-full max-w-full overflow-x-auto rounded border border-solid border-grey-4">
		<table
			{...props}
			// The last row's own bottom rule would double up against the wrapper's
			// border, leaving a 2px edge.
			className="w-full border-collapse text-left [&_tr:last-child_td]:border-b-0"
		>
			{children}
		</table>
	</div>
);
export const OscarMarkdown = ({ children, className }: OscarMarkdownProps) => (
	<div className={cn('flex w-full min-w-0 flex-col gap-s', className)}>
		<Markdown
			options={{
				forceBlock: true,
				// The agent quotes tool results, and those carry text people typed into
				// OpenWrench — work order titles, notes, supplier-authored fields. Left
				// on, `markdown-to-jsx` parses raw HTML in all of it, so an `<iframe>`,
				// a `<form>` or a `javascript:` href in any of those reaches the page.
				// Off, it renders as the text it is. `forceBlock` does not cover this,
				// and neither does the `img` override, which only stops beaconing.
				disableParsingRawHTML: true,
				// Without this every block lands inside one wrapper div, so the flex
				// `gap` above has a single child and never applies — and `p` carries
				// `mb-0`, so answers had no block spacing at all. A Fragment puts the
				// paragraphs, lists and tables directly into the flex column, where the
				// gap can space them.
				wrapper: React.Fragment,
				overrides: {
					p: { props: { className: 'bodyregular mb-0 text-character-primary' } },
					strong: { props: { className: 'bodymedium' } },
					em: { props: { className: 'italic' } },
					h1: heading('h4medium mt-s text-grey-10 first:mt-0'),
					h2: heading('h5medium mt-s text-grey-10 first:mt-0'),
					h3: heading('bodymedium mt-xs text-grey-10 first:mt-0'),
					h4: heading('bodymedium mt-xs text-grey-9 first:mt-0'),
					h5: heading('bodymedium mt-xs text-grey-9 first:mt-0'),
					h6: heading('bodymedium mt-xs text-grey-9 first:mt-0'),
					ul: {
						props: {
							className: 'bodyregular m-0 flex flex-col gap-2xs pl-l text-character-primary',
						},
					},
					ol: {
						props: {
							className: 'bodyregular m-0 flex flex-col gap-2xs pl-l text-character-primary',
						},
					},
					li: { props: { className: 'bodyregular' } },
					// An answer containing `![x](https://attacker.example/pixel)` would have
					// the browser fetch that host with no click, handing over the viewer's
					// IP and origin. Ordinary Markdown, so disabling raw HTML does not
					// help, and tool output is attacker-influenceable in principle.
					// Agent answers have no legitimate remote image: files arrive as
					// `OscarFileCard`. The alt text is kept so nothing silently vanishes.
					img: {
						component: ({ alt }: { alt?: string }) =>
							alt ? <span className="footnoteregular text-character-secondary">{alt}</span> : null,
					},
					a: {
						props: { className: 'text-[#54298e] underline', target: '_blank', rel: 'noreferrer' },
					},
					blockquote: {
						props: {
							className:
								'bodyregular m-0 border-0 border-l-2 border-solid border-grey-5 pl-s text-character-secondary',
						},
					},
					hr: { props: { className: 'w-full border-0 border-t border-solid border-grey-5' } },
					code: {
						props: { className: 'footnoteregular rounded bg-grey-3 px-2xs py-px text-grey-9' },
					},
					pre: {
						props: {
							className: 'm-0 overflow-x-auto rounded bg-grey-3 p-xs [&_code]:bg-transparent',
						},
					},
					// Tables are why this exists — a wide one scrolls in its own box
					// rather than pushing the panel sideways.
					table: { component: ScrollableTable },
					// A filled header row is what separates a table from a grid of loose
					// text; without it the first row read as data.
					thead: { props: { className: 'bg-grey-3' } },
					th: {
						props: {
							className:
								'bodymedium whitespace-nowrap border-0 border-b border-solid border-grey-5 px-s py-xs text-grey-9',
						},
					},
					td: {
						props: {
							className:
								'bodyregular border-0 border-b border-solid border-grey-4 px-s py-xs align-top text-character-primary',
						},
					},
				},
			}}
		>
			{children}
		</Markdown>
	</div>
);

export default OscarMarkdown;
